import React from 'react';
import { Users, Heart, User, Stethoscope, Brain, Shield, ArrowRight } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { psychologistData, mentalHealthInfo } from '../mock';

const ServicesSection = () => {
  const serviceIcons = {
    1: User,
    2: Users, 
    3: Heart,
    4: Brain,
    5: Stethoscope,
    6: Shield
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-charcoal-grey mb-6">
            Comprehensive Mental Health Services
          </h2>
          <p className="text-xl text-charcoal-grey max-w-3xl mx-auto leading-relaxed">
            Professional psychological services designed to help you overcome challenges, 
            build stronger relationships, and achieve lasting mental wellness.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {psychologistData.services.map((service) => {
            const IconComponent = serviceIcons[service.id];
            return (
              <Card key={service.id} className="p-8 hover:shadow-xl transition-all duration-300 border-0 shadow-lg group hover:scale-105">
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-sage-green to-soft-blue rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="text-white" size={32} />
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-xl font-bold text-charcoal-grey">{service.title}</h3>
                    <p className="text-charcoal-grey leading-relaxed">{service.description}</p>
                  </div>

                  <div className="space-y-2">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <div className="w-1.5 h-1.5 bg-lavender rounded-full flex-shrink-0"></div>
                        <span className="text-charcoal-grey">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    onClick={scrollToContact}
                    variant="outline"
                    className="w-full border-sage-green text-sage-green hover:bg-sage-green hover:text-white group-hover:border-soft-blue transition-all duration-300"
                  >
                    Learn More
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Free Programs Section */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-3xl p-12 mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-sage-green mb-4">Free Community Programs</h3>
            <p className="text-lg text-charcoal-grey max-w-2xl mx-auto">
              Supporting our community with complimentary workshops and educational sessions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {psychologistData.freePrograms.map((program, index) => (
              <Card key={index} className="p-6 bg-white/80 backdrop-blur-sm border-0 shadow-md hover:shadow-lg transition-all duration-300">
                <div className="space-y-4">
                  <Badge className="bg-green-100 text-green-800">{program.format}</Badge>
                  <h4 className="text-lg font-semibold text-gray-900">{program.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{program.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Session Types */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <Card className="p-8 bg-gradient-to-br from-blue-50 to-white border-0 shadow-lg">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="text-blue-600" size={24} />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900">In-Person Sessions</h4>
                  <p className="text-gray-600">Lokhandwala Complex & Andheri East</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 bg-blue-50 px-4 py-2 rounded-lg">
                By prior appointment
              </p>
            </div>
          </Card>
          <Card className="p-8 bg-gradient-to-br from-green-50 to-white border-0 shadow-lg">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Users className="text-green-600" size={24} />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900">Online Sessions</h4>
                  <p className="text-gray-600">Video Consultations</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 bg-green-50 px-4 py-2 rounded-lg">
                For flexible access
              </p>
            </div>
          </Card>
        </div>

        {/* Mental Health Information */}
        <Card className="p-12 bg-gradient-to-r from-gray-50 to-blue-50 border-0 shadow-lg">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Understanding Mental Health
            </h3>
            
            <div className="space-y-8">
              <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">{mentalHealthInfo.goodMentalHealth.title}</h4>
                <p className="text-gray-700 leading-relaxed">{mentalHealthInfo.goodMentalHealth.description}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Common Causes of Mental Health Challenges</h4>
                  <div className="space-y-2">
                    {mentalHealthInfo.causesOfPoorMentalHealth.map((cause, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm">{cause}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white/70 backdrop-blur-sm p-6 rounded-2xl">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Our Therapy Process</h4>
                  <div className="space-y-4">
                    {mentalHealthInfo.therapySteps.map((step) => (
                      <div key={step.step} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {step.step}
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-900 text-sm">{step.title}</h5>
                          <p className="text-gray-600 text-xs">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <Card className="p-12 bg-gradient-to-r from-blue-600 to-green-600 border-0 text-white">
            <div className="max-w-3xl mx-auto space-y-6">
              <h3 className="text-3xl font-bold">{psychologistData.offer.title}</h3>
              <p className="text-xl opacity-90">{psychologistData.offer.description}</p>
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-full font-semibold transition-all duration-200 hover:shadow-lg hover:scale-105"
              >
                Book Your Free Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;